import { Button, ButtonWithIcon, Dropdown, Icon, TableSkeletonLoader } from '@/components/atoms'
import JsonFormsWrapper from '@/components/common/jsonForms/jsonFormsWrapper/JsonFormsWrapper'
import { PageHeader } from '@/components/molecules'
import { FileUploadWithModal, PageContainer } from '@/components/organisms'
import { useDropdown } from '@/hooks/Dropdown-hook'
import { calculateEndDateByDuration } from '@/components/atoms/DatePickerInput/datePickerInput-hook'
import {
  additionalJsonSchema,
  additionalUiSchema,
  awardJsonSchema,
  awardUiSchema,
  dateJsonSchema,
  dateUiSchema,
  displayJsonSchema,
  displayUiSchema,
  pointDisplayJsonSchema,
  pointDisplayUiSchema,
  pointJsonSchema,
  pointUiSchema,
  roundUpJsonSchema,
  roundUpUiSchema,
  targetNameJsonSchema,
  targetNameUiSchema,
  availabilityWithDisplayJsonSchema,
  availabilityWithDisplayUiSchema,
} from '@/services/schema/SalesTargetJSON'
import { useAppDispatch, useAppSelector } from '@/store'
import { setSalesTargetFormData } from '@/store/slices/salesTargetSlice'
import { selectCountryCode } from '@/store/slices/countryCodeSlice'
import { COUNTRY_CODES, COUNTRY_CODES_EXCLUDE_TAX_FROM_GOAL } from '@/utils/constants/globalConstants'
import { COLOR } from '@/utils/constants/colors'
import { DROPDOWN_TYPES } from '@/utils/constants/dropdownConstants'
import { SIZE, FONT_SIZE } from '@/utils/constants/globalStyle'
import { AccordionItemV2, AccordionV2, ButtonIcon } from 'pepsico-ds'
import { useEffect, useState } from 'react'
import { PrerequisiteTarget } from '../PrerequisiteTarget'
import { TARGET_TYPES } from '@/utils/constants/saleTargetConstants'
import { downloadTemplateFile } from '@/services/downloadTemplateFile'
import { downloadCSVFiles, getDateWithTimeString } from '@/helpers/shadcn/utils'
import { Constants } from '@/utils/constants/commonConstants'
import getTargetRules from '@/helpers/targetRulesHelper'

const TargetSetUp = ({ formData, setFormData }) => {
  const countryCode = useAppSelector(selectCountryCode)
  const [openUpload, setOpenUpload] = useState(false)
  const [openTarget, setOpenTarget] = useState(false)
  const [salesDropdownJsonSchemaTypes, setSalesDropdownJsonSchemaTypes] = useState({})
  const { preRequisiteTarget } = useAppSelector(state => state.salesTarget)
  const { dropdownList, isLoading } = useDropdown({ dropdownType: DROPDOWN_TYPES.SalesTargetType })
  const dispatch = useAppDispatch()
  const validateCsvForTargetTypes = [
    TARGET_TYPES.MainTarget,
    TARGET_TYPES.ProductTarget,
    TARGET_TYPES.MonthlyTargetPrerequistite,
    TARGET_TYPES.ProductTargetPrerequisite,
    TARGET_TYPES.MonthlyTargetLayered,
    TARGET_TYPES.ProductTargetLayered,
  ];



  useEffect(() => {
    if (dropdownList?.length > 0) {
      let updatedDropdownData = JSON.parse(JSON.stringify(dateJsonSchema))
      updatedDropdownData.properties.targetType.options = [...dropdownList]
      setSalesDropdownJsonSchemaTypes(updatedDropdownData)
    }
  }, [dropdownList])

  useEffect(() => {
    if (countryCode) {
      const excludeTaxFromGoal = COUNTRY_CODES_EXCLUDE_TAX_FROM_GOAL.includes(countryCode) ? false : true
      setFormData(prev => ({ ...prev, excludeTaxFromGoal }))
    }
  }, [countryCode, setFormData])

  const handleFilterData = data => {

    let changesToApply = { ...data };

    if (changesToApply?.awardPoint) {
      changesToApply.pointsDeliveryDate = null;
      changesToApply.pointsDeliveryDateValid = null;
    }
    if (typeof changesToApply?.roundUpPointsFlag !== 'undefined' && !changesToApply.roundUpPointsFlag) {
      changesToApply.roundOffValue = null;
      changesToApply.roundOffValueValid = null;
    }
    if (changesToApply?.activeDisplay) {
      changesToApply.displayStartDate = null;
      changesToApply.displayEndDate = null;
      changesToApply.displayStartDateValid = null;
      changesToApply.displayEndDateValid = null;
    }

    const effectiveStartDateStr = changesToApply.startDate !== undefined ? changesToApply.startDate : formData.startDate;
    const effectiveDurationValue = changesToApply.durationValue !== undefined ? changesToApply.durationValue : formData.durationValue;
    const effectiveTargetType = changesToApply.targetType !== undefined ? changesToApply.targetType : formData.targetType;

    if (
      countryCode === COUNTRY_CODES.EGYPT &&
      effectiveTargetType && // Ensure targetType is defined
      (effectiveTargetType === TARGET_TYPES.MainTarget || effectiveTargetType === TARGET_TYPES.ProductTarget) &&
      effectiveStartDateStr && // Ensure startDate is a non-empty string
      effectiveDurationValue &&
      effectiveDurationValue !== 'CUSTOM'
    ) {
      const parts = String(effectiveStartDateStr).split('/');
      if (parts.length === 3) {
        const month = parseInt(parts[0], 10);
        const day = parseInt(parts[1], 10);
        const year = parseInt(parts[2], 10);

        if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
          const startDateObj = new Date(year, month - 1, day);
          startDateObj.setHours(0, 0, 0)

          const newEndDateObj = calculateEndDateByDuration(startDateObj, effectiveDurationValue);
          if (newEndDateObj) {
            const endYear = newEndDateObj.getFullYear();
            const endMonth = String(newEndDateObj.getMonth() + 1).padStart(2, '0');
            const endDay = String(newEndDateObj.getDate()).padStart(2, '0');
            changesToApply.endDate = `${endMonth}/${endDay}/${endYear}`;
            changesToApply.endDateValid = false;
          }

        } else {
          console.warn('Invalid start date format for calculation:', effectiveStartDateStr);
        }
      } else {
        console.warn('Invalid start date format for calculation:', effectiveStartDateStr);
      }
    }

    setFormData(prev => ({ ...prev, ...changesToApply }));
  };

  const handleResetFormData = (data) => {
    const isLayeredTarget =
      data?.targetType === TARGET_TYPES.MonthlyTargetLayered ||
      data?.targetType === TARGET_TYPES.ProductTargetLayered;

    const defaultFormData = {
      targetType: data?.targetType,
      targetTypeValue: data?.targetTypeValue,
      activeDisplay: true,
      excludeTaxFromGoal: !COUNTRY_CODES_EXCLUDE_TAX_FROM_GOAL.includes(countryCode),
      isNumberOfLevelsSelected: isLayeredTarget,
      isNumberOfMilestonesSelected: isLayeredTarget,
    };

    setFormData(defaultFormData);
    dispatch(setSalesTargetFormData(defaultFormData));
  };

  const isLayeredTarget =
    formData?.targetType === TARGET_TYPES.MonthlyTargetLayered || formData?.targetType === TARGET_TYPES.ProductTargetLayered
  const isRenderLayerUi = () => {
    return (
      <>
        {!isLayeredTarget && (
          <JsonFormsWrapper
            jsonschema={awardJsonSchema}
            uischema={awardUiSchema}
            data={formData}
            onChange={data => handleFilterData(data)}
          />
        )}
        {!formData?.awardPoint && (
          <div className="mt-2 z-15 relative">
            <JsonFormsWrapper
              jsonschema={pointJsonSchema}
              uischema={pointUiSchema(formData.pointsDeliveryDate, formData.targetEndDate)}
              data={formData}
              onChange={data => handleFilterData(data)}
            />
          </div>
        )}
      </>
    )
  }

  const handleFileDownload = async (typeValue, type = TARGET_TYPES.MainTarget) => {
    const { numberLevel, numberMileStone } = formData

    const body = {
      type: typeValue,
      ...(numberLevel && { levels: numberLevel }),
      ...(numberMileStone && { mileStones: numberMileStone }),
    }

    const response = await downloadTemplateFile(body)
    if (!response) return

    const fileName = `${type?.replace(/[-\s]/g, '')}_${getDateWithTimeString()}`
    downloadCSVFiles(response, fileName)
  }

  const showPointDisplay = targetType => {
    return (
      targetType == TARGET_TYPES.ProductTargetLayered ||
      targetType == TARGET_TYPES.MonthlyTargetLayered
    )
  }

  const numberOfLevels = [
    {
      displayText: '1',
      id: '1',
      isBadge: false,
    },
    {
      displayText: '2',
      id: '2',
      isBadge: false,
    },
    {
      displayText: '3',
      id: '3',
      isBadge: false,
    },
  ]

  const milestones = [
    {
      displayText: '2',
      id: '1',
      isBadge: false,
    },
    {
      displayText: '3',
      id: '2',
      isBadge: false,
    },
  ]
  return (
    <PageContainer data-testid="nbas-list-page">
      <>
        <div className="flex items-center justify-between">
          <PageHeader fontSize={FONT_SIZE.LG} title={'Target Setup'} />
        </div>
        <div className="flex flex-row items-center">
          {isLoading && <TableSkeletonLoader rows={2} spacing={30} data-testid="dropdown-loader" />}
          {salesDropdownJsonSchemaTypes && Object.keys(salesDropdownJsonSchemaTypes)?.length > 0 ? (
            <div className="flex flex-row items-center">
              <JsonFormsWrapper
                jsonschema={salesDropdownJsonSchemaTypes}
                uischema={dateUiSchema(formData?.targetType)}
                data={formData}
                onChange={data => {
                  handleResetFormData(data)
                }}
              />

              <div className="ml-6 mt-8">
                <JsonFormsWrapper
                  jsonschema={targetNameJsonSchema}
                  uischema={targetNameUiSchema}
                  data={formData}
                  onChange={data => {
                    handleFilterData(data)
                  }}
                />
              </div>
            </div>
          ) : null}

          <div className="ml-6 mt-2">
            {showPointDisplay(formData?.targetType) ? (
              <JsonFormsWrapper
                jsonschema={pointDisplayJsonSchema}
                uischema={pointDisplayUiSchema}
                data={formData}
                onChange={data => handleFilterData(data)}
              />
            ) : null}
          </div>
        </div>
        {formData?.targetType == undefined ||
          formData.targetType == '' ||
          formData?.targetType == TARGET_TYPES.MainTarget ||
          formData?.targetType == TARGET_TYPES.ProductTarget ||
          formData?.targetType == TARGET_TYPES.MonthlyTargetPrerequistite ||
          formData?.targetType == TARGET_TYPES.ProductTargetPrerequisite ? (
          <div className="border-height mb-6 rounded-lg border-2 border-solid">
            <p className="pb-4 pl-3 pt-2 text-lg font-bold">Target</p>
            <div className="butt-icon mx-3 flex">
              <ButtonIcon
                icon="file_download"
                isDark
                onClick={() => handleFileDownload(formData?.targetTypeValue, formData?.targetType)}
                size="small"
                data-testid="download-template-btn"
              />
              {formData?.targetMediaFile?.length == 1 ? (
                <div className="main_upload-title ml-2  flex items-center rounded-lg px-2 py-3.5 text-xs">
                  <Icon alt="file" className="insert_file_icon" icon="insert_drive_file" size="small" />
                  <p className="w-[105px] truncate pl-2">{formData?.targetMediaFile?.[0]}</p>
                  <Icon
                    alt="cached"
                    className="cach_ed_icon"
                    icon="cached"
                    size="small"
                    data-testid="file-delete-icon"
                    onClick={() => setOpenUpload(true)}
                  />
                </div>
              ) : (
                <ButtonWithIcon
                  uiSchema={{
                    className: 'butt-size ml-2',
                    placeholder: 'Add Target Name',
                    isDark: true,
                    title: 'Upload Target Template',
                    size: SIZE.SMALL,
                    variant: COLOR.PRIMARY,
                  }}
                  iconLeading="upload"
                  handleButton={() => setOpenUpload(true)}
                />
              )}
            </div>
          </div>
        ) : (
          <div className="border-target-height mb-6 rounded-lg border-2 border-solid pb-3">
            <p className="pb-4 pl-3 pt-2 text-lg font-bold">Target</p>
            <div className="butt-icon mx-3 flex items-end">
              <Dropdown
                key={'number-level'}
                isJsonWrap={false}
                isRequired={'required'}
                value={numberOfLevels.filter(item => item?.displayText == formData?.numberLevel)}
                childList={numberOfLevels}
                dropdownHeader={''}
                isClearable={true}
                className="number-level"
                isDark={true}
                label={'Number of Levels *'}
                selection={'single'}
                size={'medium'}
                placeholder={'Select'}
                editable={true}
                onKeyDown={e => {
                  e.preventDefault()
                }}
                updateValue={val => {
                  setFormData(prev => ({
                    ...prev,
                    ...{
                      numberLevel: val?.[0]?.displayText,
                      isNumberOfLevelsSelected: val?.[0]?.displayText ? false : true,
                    },
                  }))
                }}
              />
              <Dropdown
                key={'milestones'}
                isJsonWrap={false}
                isRequired={'required'}
                value={milestones.filter(item => item.displayText == formData?.numberMileStone)}
                isClearable={true}
                childList={milestones}
                dropdownHeader={''}
                className="number-miles px-4"
                isDark={true}
                label={'Number of Milestones *'}
                selection={'single'}
                onKeyDown={e => {
                  e.preventDefault()
                }}
                updateValue={val => {
                  setFormData(prev => ({
                    ...prev,
                    ...{
                      numberMileStone: val?.[0]?.displayText,
                      isNumberOfMilestonesSelected: val?.[0]?.displayText ? false : true,
                    },
                  }))
                }}
                size={'medium'}
                placeholder={'Select'}
              />

              <div className="ml-4">
                <ButtonIcon
                  icon="file_download"
                  isDark
                  onClick={() => handleFileDownload(formData?.targetTypeValue, formData?.targetType)}
                  size="medium"
                  type="default"
                  data-testid="file_download"
                />
              </div>
            </div>
            {formData?.targetMediaFile?.length == 1 ? (
              <div className="layer_upload-title flex  items-center rounded-lg px-2 py-3.5 text-xs">
                <Icon alt="file" className="insert_file_icon" icon="insert_drive_file" size="small" />
                <p className="w-[135px] truncate pl-2">{formData?.targetMediaFile?.[0]}</p>
                <Icon
                  alt="cached"
                  className="cach_ed_icon"
                  icon="cached"
                  size="small"
                  onClick={() => setOpenUpload(true)}
                />
              </div>
            ) : (
              <ButtonWithIcon
                uiSchema={{
                  className: 'butt-size ml-3 mt-3',
                  placeholder: 'Upload Target Template',
                  title: 'Upload Target Template',
                  size: SIZE.SMALL,
                  variant: COLOR.PRIMARY,
                }}
                iconLeading="upload"
                handleButton={() => setOpenUpload(true)}
              />
            )}
          </div>
        )}
        <div className="choose-button mb-6 mt-8 flex">
          {formData?.isPrerequisite ? (
            <>
              {preRequisiteTarget ? (
                <div className="prereq-title flex items-center rounded-lg p-3.5 text-xs">
                  <p>{preRequisiteTarget}</p>
                  <Icon
                    alt="cached"
                    className="cach_ed_icon"
                    icon="cached"
                    size="small"
                    onClick={() => setOpenTarget(true)}
                  />
                </div>
              ) : (
                <Button
                  uiSchema={{
                    title: 'Choose Target',
                    size: SIZE.SMALL,
                    variant: COLOR.SECONDARY,
                  }}
                  handleButton={() => setOpenTarget(true)}
                />
              )}
            </>
          ) : (
            ''
          )}
        </div>
        <div className="accord">
          <hr className="border-gray" />
          <AccordionV2 className="accord" onUpdate={function Qa() { }} presentation="no-background" separator>
            <AccordionItemV2 label="Additional Options" onUpdate={function Qa() { }} presentation="box-shadow">
              <JsonFormsWrapper
                jsonschema={additionalJsonSchema}
                uischema={additionalUiSchema}
                data={formData}
                onChange={data => handleFilterData(data)}
              />
              {formData?.roundUpPointsFlag && (
                <JsonFormsWrapper
                  jsonschema={roundUpJsonSchema}
                  uischema={roundUpUiSchema}
                  data={formData}
                  onChange={data => handleFilterData(data)}
                />
              )}
            </AccordionItemV2>
          </AccordionV2>
        </div>
        <div>
          <p className="mb-5 mt-4 text-lg font-bold">Availability</p>
          <div className="mb-8 mt-2 z-55 relative">
            <JsonFormsWrapper
              jsonschema={availabilityWithDisplayJsonSchema(countryCode, formData?.targetType, formData)}
              uischema={availabilityWithDisplayUiSchema(countryCode, formData?.targetType, formData?.startDate || '', formData?.endDate, formData)}
              data={formData}
              onChange={data => handleFilterData(data)}
            />
          </div>
          <hr className="my-6 border-gray" />
          <div className="mb-8 z-25 relative">
            <JsonFormsWrapper
              jsonschema={displayJsonSchema}
              uischema={displayUiSchema(formData?.endDate)}
              data={formData}
              onChange={data => handleFilterData(data)}
            />
          </div>
          {isRenderLayerUi()}
        </div>
        <FileUploadWithModal
          openUpload={openUpload}
          setOpenUpload={setOpenUpload}
          setFormData={setFormData}
          formData={formData}
          uploadFor={Constants.SalesTargetMedia}
          fileType="csv"
          isCsvValidationRequired={validateCsvForTargetTypes.includes(formData?.targetType)} // Check csv file validation
          customRules={getTargetRules({ targetType: formData?.targetType, numberLevel: formData?.numberLevel, numberMileStone: formData?.numberMileStone, countryCode: countryCode })}
        />
        <PrerequisiteTarget openTarget={openTarget} setOpenTarget={setOpenTarget} setFormData={setFormData} />
      </>
    </PageContainer>
  )
}

export default TargetSetUp
